import { useRefreshTick } from "../lib/useAutoRefresh";
import React, { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRoles } from "../lib/useRoles";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";
import { ResendInviteButton } from "../components/LoginTools";
import { TeammatePermissions, StaffPermsPanel, TeammateClients } from "../components/StaffPermissions";
import { useSort, SortBtn } from "../lib/sortable";
import { NotifyKindChips } from "../components/NotifyKindChips";

const STAFF_A = { name: (s) => s.name || "", role: (s) => s.staff_role || "master" };


export function StaffPanel() {
  const { user } = useAuth();
  const ROLES = useRoles().staff.map((r) => ({ v: r.key, label: r.label }));
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", staff_role: "officer" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const [showInvite, setShowInvite] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [kindOptions, setKindOptions] = useState([]);

  const load = () => api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
  const tick = useRefreshTick();
  useEffect(() => { load(); api.get("/notification-kinds").then((r) => setKindOptions(r.data.kind_options || [])).catch(() => {}); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const { sorted, sort, toggle } = useSort(staff, STAFF_A, "name", "asc");

  const setKinds = async (uid, kinds) => {
    try {
      await api.put(`/staff/${uid}/notify-kinds`, { kinds });
      toast.success("Notification types updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const invite = async () => {
    setBusy(true);
    try {
      const r = await api.post("/staff/invite", form);
      toast.success(r.data?.email_sent ? "Invite email sent" : "Staff member added (email not sent — check config)");
      setForm({ name: "", email: "", staff_role: "officer" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const setRole = async (uid, staff_role) => {
    try { await api.put(`/staff/${uid}/role`, { staff_role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (uid, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try { await api.delete(`/staff/${uid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="staff-page">
      {showInvite && <div className="border border-border bg-white p-6 mb-6">
        <h2 className="font-bold flex items-center gap-2"><UserPlus className="h-4 w-4" /> Invite a teammate</h2>
        <p className="text-xs text-muted-foreground mt-1">An email invite with a secure set-password link is sent. The link expires in 72 hours.</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <input data-testid="staff-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-input px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          <input data-testid="staff-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-input px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          <select data-testid="staff-role" value={form.staff_role} onChange={(e) => setForm({ ...form, staff_role: e.target.value })} className="border border-input px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
        </div>
        <button data-testid="staff-invite-button" disabled={busy || !form.name || !form.email} onClick={invite} className="mt-4 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">{busy ? "Sending…" : "Send invite"}</button>
      </div>}

      <div className="border border-border bg-white mb-6">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Users className="h-4 w-4" /><h2 className="font-bold">Team members</h2>
          <span className="text-xs text-muted-foreground">Expand a teammate to set assigned clients and per-area overrides</span>
          <button data-testid="staff-show-invite" onClick={() => setShowInvite((v) => !v)} className="ml-auto inline-flex items-center gap-1.5 border border-black bg-white px-3 py-1.5 text-xs font-medium hover:bg-black hover:text-white transition-colors"><UserPlus className="h-3.5 w-3.5" /> {showInvite ? "Close invite" : "Invite teammate"}</button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <th className="px-5 py-3"><SortBtn label="Name" col="name" sort={sort} onSort={toggle} /></th>
              <th className="px-5 py-3 text-right"><SortBtn label="Role" col="role" sort={sort} onSort={toggle} /></th>
              <th className="px-5 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <React.Fragment key={s.user_id}>
              <tr data-testid={`staff-row-${s.user_id}`} className="border-b border-border last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium flex items-center gap-2">
                    {(s.staff_role || "master") !== "master" && <span data-testid={`staff-clients-badge-${s.user_id}`} className="order-last text-[9px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">{s.assigned_company_ids?.length ? `${s.assigned_company_ids.length} client${s.assigned_company_ids.length === 1 ? "" : "s"}` : "all clients"}</span>}
                    {(s.staff_role || "master") !== "master" ? <button data-testid={`staff-perms-toggle-${s.user_id}`} onClick={() => setOpen((o) => ({ ...o, [s.user_id]: !o[s.user_id] }))} className="text-muted-foreground hover:text-black" title="Assigned clients & overrides">{open[s.user_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button> : <span className="w-4" />}
                    {s.name}
                    {s.status === "pending" && <span data-testid={`staff-pending-${s.user_id}`} className="text-[10px] font-mono uppercase tracking-widest bg-amber-100 text-amber-700 px-1.5 py-0.5">Pending</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{s.email}</div>
                  <div className="mt-1.5"><NotifyKindChips id={`st-${s.user_id}`} kinds={s.notify_kinds || null} options={kindOptions} canEdit onChange={(next) => setKinds(s.user_id, next)} sourceNote="automatic notifications · all assigned clients" /></div>
                </td>
                <td className="px-5 py-3 text-right">
                  <select data-testid={`staff-role-${s.user_id}`} value={s.staff_role || "master"} disabled={s.user_id === user?.user_id} onChange={(e) => setRole(s.user_id, e.target.value)} className="border border-input px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50">
                    {ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                  <ResendInviteButton u={s} onDone={load} />
                  {s.user_id !== user?.user_id && (
                    <button data-testid={`staff-delete-${s.user_id}`} onClick={() => remove(s.user_id, s.name)} className="text-muted-foreground hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                  )}
                  </div>
                </td>
              </tr>
              {open[s.user_id] && (s.staff_role || "master") !== "master" && (
                <tr data-testid={`staff-perms-row-${s.user_id}`} className="border-b border-border bg-neutral-50/60">
                  <td colSpan={3} className="px-5 py-4">
                    <div className="mb-4 pb-4 border-b border-border"><TeammateClients userId={s.user_id} staffRole={s.staff_role || "master"} assigned={s.assigned_company_ids} onSaved={load} /></div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Area access · {ROLES.find((r) => r.v === (s.staff_role || "master"))?.label} role defaults + this teammate's overrides</div>
                    <TeammatePermissions staffRole={s.staff_role || "master"} userId={s.user_id} overrides={s.perm_overrides} onSaved={load} />
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-border bg-white">
        <button data-testid="staff-toggle-matrix" onClick={() => setShowMatrix((v) => !v)} className="w-full px-5 py-3 flex items-center gap-2 text-left hover:bg-neutral-50 transition-colors">
          {showMatrix ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-bold">Role defaults</span>
          <span className="text-xs text-muted-foreground">What Payroll Officer and Read-only roles can view/edit by default (per-teammate overrides above take precedence)</span>
        </button>
        {showMatrix && <div className="px-5 pb-5"><StaffPermsPanel embedded /></div>}
      </div>
    </div>
  );
}

export default function Staff() {
  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight">Staff & Permissions</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Postings Inc team access</p>
      </div>
      <StaffPanel />
    </div>
  );
}
